import type { TMFELoaderReact, TMFELoaderElement } from "@sinch/mfe-loader";
import { useEffect, useRef, useState, type FC } from "react";

type KebabToCamelCase<S extends string> =
  S extends `${infer P1}-${infer P2}${infer P3}`
    ? `${P1}${Uppercase<P2>}${KebabToCamelCase<P3>}`
    : S;

type KebabToCamelProps<T> = {
  [K in keyof T as K extends string ? KebabToCamelCase<K> : K]: T[K];
};

interface MfeLoaderProps extends KebabToCamelProps<TMFELoaderReact> {
  Fallback?: FC<{ error?: unknown }>;
  LoadingIndicator?: FC;
  style?: React.CSSProperties;
}

/**
 * A wrapper for the <mfe-loader> component to include some extra functionality
 */
export const MFELoaderWrapper: FC<MfeLoaderProps> = (props) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const mfeLoaderRef = useRef<TMFELoaderElement>(null);

  useEffect(() => {
    const mountElement = mfeLoaderRef.current?.mountElement;
    if (mountElement) {
      Object.assign(mountElement.style, props.style ?? {});
    }
  }, [props.style]);

  if (hasError && props.Fallback) {
    return <props.Fallback />;
  }

  return (
    <>
      <mfe-loader
        ref={mfeLoaderRef}
        name={props.name}
        module={props.module}
        base-path={props.basePath}
        style={props.style}
        on-error={(e) => {
          console.error(`Error loading MFE ${e.detail.mfe}`, e.detail.message);
          setHasError(true);
          setIsLoading(false);
          props.onError?.(e);
        }}
        on-mount={(e) => {
          setIsLoading(false);
          props.onMount?.(e);
        }}
        on-load={(e) => {
          props.onLoad?.(e);
        }}
      />
      {isLoading && props.LoadingIndicator && <props.LoadingIndicator />}
    </>
  );
};
